import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { CustomerContext, type CustomerContextValue } from '@/contexts/CustomerContext';
import { useBusiness } from '@/hooks/useBusiness';
import { useDataStore } from '@/hooks/useDataStore';
import { useSettings } from '@/hooks/useSettings';
import { withLimit } from '@/storage';
import type { CreateInput, Customer, ID, UpdateInput } from '@/types';

const NO_CUSTOMERS: Customer[] = [];

export function CustomerProvider({ children }: PropsWithChildren) {
  const store = useDataStore();
  const { plan } = useSettings();
  const { activeBusiness } = useBusiness();
  const businessId = activeBusiness?.id ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  /** The business `customers` was loaded for — see `current` below. */
  const [loadedFor, setLoadedFor] = useState<ID | null>(null);

  /** Plan limits are applied at the persistence boundary, not in the views. */
  const repository = useMemo(
    () => withLimit(store.customers, () => plan.limits.savedCustomers, 'customers'),
    [store, plan.limits.savedCustomers],
  );

  useEffect(() => {
    if (!businessId) {
      setCustomers([]);
      setLoadedFor(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    void repository.list().then((all) => {
      if (cancelled) return;
      setCustomers(all.filter((customer) => customer.businessId === businessId));
      setLoadedFor(businessId);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [repository, businessId]);

  const createCustomer = useCallback<CustomerContextValue['createCustomer']>(
    async (input) => {
      if (!businessId) throw new Error('No active business — cannot save a customer.');
      const created = await repository.create({ ...input, businessId } as CreateInput<Customer>);
      setCustomers((current) => [...current, created]);
      return created;
    },
    [repository, businessId],
  );

  const updateCustomer = useCallback(
    async (id: ID, patch: UpdateInput<Customer>) => {
      const next = await repository.update(id, patch);
      setCustomers((current) => current.map((customer) => (customer.id === id ? next : customer)));
    },
    [repository],
  );

  const deleteCustomer = useCallback(
    async (id: ID) => {
      await repository.remove(id);
      setCustomers((current) => current.filter((customer) => customer.id !== id));
    },
    [repository],
  );

  const toggleFavourite = useCallback(
    async (id: ID) => {
      const target = customers.find((customer) => customer.id === id);
      if (!target) return;
      await updateCustomer(id, { favourite: !target.favourite });
    },
    [customers, updateCustomer],
  );

  // Right after a business switch the previous business's list is still in
  // state until the reload lands. It is never exposed: until then there are
  // no customers, and the provider says it is loading.
  const current = loadedFor === businessId;
  const visible = current ? customers : NO_CUSTOMERS;

  const limit = plan.limits.savedCustomers;
  const remainingSlots = limit === null ? null : Math.max(0, limit - visible.length);

  const value = useMemo<CustomerContextValue>(
    () => ({
      customers: visible,
      isLoading: isLoading || !current,
      remainingSlots,
      canAddCustomer: remainingSlots === null || remainingSlots > 0,
      createCustomer,
      updateCustomer,
      deleteCustomer,
      toggleFavourite,
    }),
    [visible, isLoading, current, remainingSlots, createCustomer, updateCustomer, deleteCustomer, toggleFavourite],
  );

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}
